/**
 * Utilitário centralizado para máscaras de strings (Telefone, CPF, etc.)
 */

/**
 * Remove todos os caracteres não numéricos de uma string
 */
export function removeNonDigits(value: string | null | undefined): string {
    if (!value) return '';
    return value.replace(/\D/g, '');
}

/**
 * Aplica máscara de telefone brasileiro (fixo ou celular)
 * Formatos: (DD) 0000-0000 ou (DD) 00000-0000
 */
export function maskPhone(value: string | null | undefined): string {
    if (!value) return '';

    // Remove tudo que não é dígito
    let digits = value.replace(/\D/g, '');

    // Limita a 11 dígitos
    if (digits.length > 11) {
        digits = digits.substring(0, 11);
    }

    if (digits.length === 11) {
        // Celular: (99) 99999-9999
        return digits.replace(/^(\d{2})(\d{5})(\d{4}).*/, '($1) $2-$3');
    } else if (digits.length === 10) {
        // Fixo: (99) 9999-9999
        return digits.replace(/^(\d{2})(\d{4})(\d{4}).*/, '($1) $2-$3');
    } else if (digits.length > 2) {
        // Formatação parcial enquanto digita
        if (digits.length > 6) {
            return digits.replace(/^(\d{2})(\d{4})(\d{0,5})/, '($1) $2-$3');
        } else {
            return digits.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
        }
    } else if (digits.length > 0) {
        return digits.replace(/^(\d*)/, '($1');
    }

    return digits;
}

/**
 * Retorna apenas os dígitos para armazenamento no banco
 */
export function unmaskPhone(value: string | null | undefined): string {
    return removeNonDigits(value);
}

/**
 * Aplica máscara de CPF oculta: 000.***.***-00
 */
export function maskCpfHidden(value: string | null | undefined): string {
    if (!value) return '';
    const digits = removeNonDigits(value);
    if (digits.length !== 11) return digits; // Retorna sem máscara se não for CPF válido (11 dígitos)
    
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.***.***-$4');
}

export type ProxyProtocol = 'http' | 'https' | 'socks4' | 'socks5';

export interface ParsedProxyConnection {
    protocol: ProxyProtocol;
    host: string;
    port: number;
    username: string;
    password: string;
}

const VALID_PROXY_PROTOCOLS = new Set<ProxyProtocol>(['http', 'https', 'socks4', 'socks5']);

/**
 * Mascara um endereço IPv4, IPv6 ou hostname de gateway para exibição e logs seguros.
 * Ex: 177.54.120.88 -> 177.54.***.***
 */
export function maskIpAddress(ipOrHost: string | null | undefined): string {
    if (!ipOrHost) return '';
    const trimmed = ipOrHost.trim().replace(/^\[|\]$/g, '');
    if (!trimmed) return '';

    const ipv4Match = trimmed.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
    if (ipv4Match) {
        return `${ipv4Match[1]}.${ipv4Match[2]}.***.***`;
    }

    if (trimmed.includes(':')) {
        const parts = trimmed.split(':').filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0]}:${parts[1]}:****:****`;
        }
        return '****:****';
    }

    const domainParts = trimmed.split('.');
    if (domainParts.length >= 3) {
        return `${domainParts[0]}.***.${domainParts[domainParts.length - 1]}`;
    }
    if (domainParts.length === 2) {
        const first = domainParts[0];
        const prefix = first.length > 2 ? first.slice(0, 2) : first.slice(0, 1);
        return `${prefix}***.${domainParts[1]}`;
    }

    return trimmed.length > 4 ? `${trimmed.slice(0, 2)}***` : '***';
}

/**
 * Valida se o endpoint de proxy é seguro (proteção Anti-SSRF contra loopback/redes internas).
 */
export function isValidProxyEndpoint(
    host: string | null | undefined,
    port: number | null | undefined
): { valid: boolean; reason?: string } {
    const cleanHost = (host || '').trim().toLowerCase().replace(/^\[|\]$/g, '');
    const numericPort = Number(port);

    if (!cleanHost) {
        return { valid: false, reason: 'Informe o Host ou IP do proxy.' };
    }
    if (/[\s\/\\?#@]/.test(cleanHost)) {
        return { valid: false, reason: 'O Host do proxy não deve conter protocolo, barras ou credenciais.' };
    }
    if (!Number.isInteger(numericPort) || numericPort < 1 || numericPort > 65535) {
        return { valid: false, reason: 'A porta deve ser um número entre 1 e 65535.' };
    }

    const blockedExact = new Set(['localhost', '0.0.0.0', '::1', '0:0:0:0:0:0:0:1', 'metadata.google.internal']);
    if (
        blockedExact.has(cleanHost) ||
        cleanHost.endsWith('.local') ||
        cleanHost.endsWith('.internal') ||
        cleanHost.startsWith('fe80:') ||
        cleanHost.startsWith('fc00:') ||
        cleanHost.startsWith('fd00:')
    ) {
        return { valid: false, reason: 'Endereço interno/local bloqueado por segurança.' };
    }

    const ipv4 = cleanHost.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
    if (ipv4) {
        const octets = ipv4.slice(1, 5).map(Number);
        if (octets.some(o => o < 0 || o > 255)) {
            return { valid: false, reason: 'Endereço IPv4 inválido.' };
        }
        const [a, b] = octets;
        const isPrivateOrReserved =
            a === 0 ||
            a === 10 ||
            a === 127 ||
            (a === 169 && b === 254) ||
            (a === 172 && b >= 16 && b <= 31) ||
            (a === 192 && b === 168) ||
            (a === 100 && b >= 64 && b <= 127) ||
            a >= 224;

        if (isPrivateOrReserved) {
            return { valid: false, reason: 'IP privado ou reservado não é permitido como proxy externo.' };
        }
    }

    return { valid: true };
}

/**
 * Interpreta strings de conexão de proxy nos formatos mais usados por provedores:
 * - protocol://user:pass@host:port
 * - user:pass@host:port
 * - host:port:user:pass
 * - host:port
 */
export function parseProxyString(raw: string | null | undefined): ParsedProxyConnection | null {
    if (!raw) return null;
    let input = raw.trim();
    if (!input) return null;

    let protocol: ProxyProtocol = 'http';
    const schemeMatch = input.match(/^([a-zA-Z0-9]+):\/\/(.+)$/);
    if (schemeMatch) {
        const candidate = schemeMatch[1].toLowerCase() as ProxyProtocol;
        if (!VALID_PROXY_PROTOCOLS.has(candidate)) return null;
        protocol = candidate;
        input = schemeMatch[2].trim();
    }

    input = input.replace(/\/+$/, '');

    if (input.includes('@')) {
        const atIndex = input.lastIndexOf('@');
        const authPart = input.slice(0, atIndex);
        const hostPart = input.slice(atIndex + 1);
        const colonIdx = authPart.indexOf(':');
        const username = colonIdx >= 0 ? decodeURIComponent(authPart.slice(0, colonIdx)) : decodeURIComponent(authPart);
        const password = colonIdx >= 0 ? decodeURIComponent(authPart.slice(colonIdx + 1)) : '';

        const hostPort = splitHostAndPort(hostPart);
        if (!hostPort) return null;

        return {
            protocol,
            host: hostPort.host,
            port: hostPort.port,
            username,
            password
        };
    }

    const segments = input.split(':');
    if (segments.length >= 4 && /^\d{1,5}$/.test(segments[1])) {
        const port = Number(segments[1]);
        if (port < 1 || port > 65535) return null;
        return {
            protocol,
            host: segments[0].trim(),
            port,
            username: segments[2],
            password: segments.slice(3).join(':')
        };
    }

    const hostPort = splitHostAndPort(input);
    if (!hostPort) return null;

    return {
        protocol,
        host: hostPort.host,
        port: hostPort.port,
        username: '',
        password: ''
    };
}

function splitHostAndPort(hostPort: string): { host: string; port: number } | null {
    const trimmed = hostPort.trim();
    const lastColon = trimmed.lastIndexOf(':');
    if (lastColon <= 0) return null;

    const host = trimmed.slice(0, lastColon).trim().replace(/^\[|\]$/g, '');
    const portStr = trimmed.slice(lastColon + 1).trim();
    if (!host || !/^\d{1,5}$/.test(portStr)) return null;

    const port = Number(portStr);
    if (port < 1 || port > 65535) return null;
    return { host, port };
}

/**
 * Remove credenciais embutidas em URLs e mascara IPs brutos em logs/mensagens de erro.
 */
export function sanitizeSensitiveProxyData(rawText: string | null | undefined): string {
    if (!rawText) return '';
    return rawText
        .replace(/([a-zA-Z0-9]+:\/\/)([^\/\s:@]+):([^\/\s@]+)@/g, '$1***:***@')
        .replace(/\b(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\b/g, '$1.$2.***.***');
}


